import { create } from 'zustand';
import { 
  ModuleTypeEnum, 
  PermissionActionEnum
} from '@scrambl/shared';

// Enhanced types for permission assignments with included relations
interface ExtendedPermissionAssignment {
  id: string;
  memberId: string;
  permissionDefinitionId: string;
  permissionDefinition?: {
    id: string;
    name: string;
    moduleType: ModuleTypeEnum;
    allowedActions: PermissionActionEnum[];
  };
}

interface ExtendedPermissionSetAssignment {
  id: string;
  memberId: string;
  permissionSetId: string;
  permissionSet?: {
    id: string;
    name: string;
    permissionSetItems?: Array<{
      id?: string;
      permissionSetId: string;
      permissionDefinitionId: string;
      permissionDefinition?: {
        id: string;
        name: string;
        moduleType: ModuleTypeEnum;
        allowedActions: PermissionActionEnum[];
      };
    }>;
  };
}

// State interface - UI state only
interface PermissionsState {
  // Member permissions data (set by usePermissions hook from query data)
  memberPermissions: {
    directPermissions: ExtendedPermissionAssignment[];
    permissionSets: ExtendedPermissionSetAssignment[];
  } | null;
  
  // Client-side permission cache
  userPermissionCache: Record<string, boolean>;

  // Helper functions
  hasPermission: (moduleType: ModuleTypeEnum, action: string) => boolean;
  clearCache: () => void;
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  // Initial state
  memberPermissions: null,
  userPermissionCache: {},
  
  // Check if current user has a specific permission
  // This is a client-side check and should be used for UI purposes only
  // Note: memberPermissions should be set by usePermissions hook from TanStack Query data
  hasPermission: (moduleType: ModuleTypeEnum, action: string) => {
    const { memberPermissions, userPermissionCache } = get();
    
    // Check cache first
    const cacheKey = `${moduleType}:${action}`;
    if (userPermissionCache[cacheKey] !== undefined) {
      return userPermissionCache[cacheKey];
    }
    
    // If no permissions data is loaded, default to false
    if (!memberPermissions) {
      return false;
    }
    
    // Check direct permissions (with null safety)
    const hasDirectPermission = memberPermissions.directPermissions?.some(
      perm => perm.permissionDefinition?.moduleType === moduleType &&
              perm.permissionDefinition?.allowedActions?.includes(action as PermissionActionEnum)
    ) || false;
    
    if (hasDirectPermission) {
      // Cache the result asynchronously to avoid state update during render
      setTimeout(() => {
        set(state => ({
          userPermissionCache: {
            ...state.userPermissionCache,
            [cacheKey]: true
          }
        }));
      }, 0);
      return true;
    }
    
    // Check permissions via permission sets (with null safety)
    const hasSetPermission = memberPermissions.permissionSets?.some(
      setAssignment => setAssignment.permissionSet?.permissionSetItems?.some(
        item => item.permissionDefinition?.moduleType === moduleType &&
                item.permissionDefinition?.allowedActions?.includes(action as PermissionActionEnum)
      )
    ) || false;
    
    // Cache the result asynchronously to avoid state update during render
    setTimeout(() => {
      set(state => ({
        userPermissionCache: {
          ...state.userPermissionCache,
          [cacheKey]: hasSetPermission
        }
      }));
    }, 0);
    
    return hasSetPermission;
  },
  
  // Clear all cached data
  clearCache: () => {
    set({
      memberPermissions: null,
      userPermissionCache: {},
    });
  }
}));
